import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import ReactInputMask from 'react-input-mask';
import WarningIcon from '@material-ui/icons/ReportProblem';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { RootState } from '../../../../../redux/root';
import { getServerTime } from '../../../../../api/inspections';
import { validateInspectionDate } from '../../../utils';
import {
  setInspectionApiResults,
  setPermitInspectionsList,
  setSelectedPermit,
} from '../../../../../redux/slices/inspectionsSlice';
import {
  setContact,
  setContactPhone,
  setErrors,
  setNotes,
} from '../../../../../redux/slices/InspectionsReportSlice';
import { InspectionStatuses } from '../../../../../constants';
import { useUpdateInspectionsMutation } from '../../../../../redux/rtkQuery/apiSlice';
import { ModalDefaultDraggable } from '../../../../../molecules';
import { Loader } from '../../../../../atoms';
import DatePickerControl from '../../Common/Controls/DatePickerControl';
import ContactDropdown from './Controls/ContactDropdown';

const HOLD_MESSAGE =
  'Inspections cannot be scheduled. This permit has a hold on it — check conditions or contact the office for more information.';

// "Ready For Scheduling" — same status the single Request flow assigns.
const READY_FOR_SCHEDULING_STATUS_ID = 29;

interface Props {
  selectedInspections: any[];
  disableFromHold: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const splitTypeName = (token: string) => {
  if (!token) return { code: '', description: '' };
  const parts = token.split(' ');
  return {
    code: parts[0],
    description: token.substring(parts[0].length).trim(),
  };
};

const BulkRequestInspectionsPopup: FC<Props> = ({
  selectedInspections,
  disableFromHold,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const { selectedPermit, permitInspectionsList, isApiError, apiMessage } =
    useSelector((s: RootState) => s.inspections);
  const { contact, contactPhone, notes, errors, allContacts } = useSelector(
    (s: RootState) => s.inspectionReports,
  );
  const schedules = useSelector((s: RootState) => s.general.schedules);
  const [updateInspections] = useUpdateInspectionsMutation();

  const [inspectionDate, setInspectionDate] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);

  // Fresh form each time the popup opens — bulk requests start from a clean
  // contact/notes/phone rather than inheriting a prior single-request session.
  useEffect(() => {
    dispatch(setContact({}));
    dispatch(setContactPhone(''));
    dispatch(setNotes(''));
    dispatch(setErrors({}));
    dispatch(setInspectionApiResults({ apiMessage: undefined, isApiError: false }));
    return () => {
      dispatch(setInspectionApiResults({ apiMessage: undefined, isApiError: false }));
    };
  }, []);

  // Keep the editable phone in sync with the picked contact (mirrors the
  // single-request InspectionsControls behaviour).
  useEffect(() => {
    dispatch(setContactPhone(contact?.phoneNumber || ''));
  }, [contact]);

  const count = selectedInspections.length;
  const inspectionLabel = count === 1 ? 'inspection' : 'inspections';

  const setDateValue = (value: string) => {
    if (errors.inspectionDate) {
      const _errors = { ...errors };
      delete _errors.inspectionDate;
      dispatch(setErrors(_errors));
    }
    setInspectionDate(value);
    dispatch(setInspectionApiResults({ apiMessage: undefined, isApiError: false }));
  };

  const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setContactPhone(e.target.value));
    dispatch(setErrors({ ...errors, contactPhone: null }));
  };

  const onSubmit = async () => {
    if (done) {
      onClose();
      return;
    }

    const estTime: any = await getServerTime();
    const now = estTime?.data?.estTime ? new Date(estTime.data.estTime) : new Date();

    let validation = validateInspectionDate(new Date(inspectionDate), schedules, now);
    let _errors = { ...errors };
    if (validation !== null) {
      _errors = { ..._errors, inspectionDate: validation };
      dispatch(setErrors(_errors));
    }

    const phoneRegex = /^[0-9][0-9][0-9]-[0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]$/;
    if (!phoneRegex.test(contactPhone)) {
      validation = 'Phone has to follow the pattern ###-###-####';
      _errors = { ..._errors, contactPhone: validation };
      dispatch(setErrors(_errors));
    }

    if (validation !== null) return;

    const formattedDate = format(new Date(inspectionDate), 'MM/dd/yyyy');
    const basePayload = {
      contact: contact.id,
      phone_number: contact.phoneNumber,
      information: notes,
      inspection_date: formattedDate,
      status: READY_FOR_SCHEDULING_STATUS_ID,
    };

    setSubmitting(true);
    try {
      const results: any[] = await Promise.all(
        selectedInspections.map(insp => updateInspections({ ...basePayload, id: insp.id })),
      );

      const updatedById: Record<number, any> = {};
      results.forEach(res => {
        if (res?.data?.id) {
          updatedById[res.data.id] = res.data;
        }
      });

      const successCount = Object.keys(updatedById).length;
      if (successCount === 0) {
        dispatch(
          setInspectionApiResults({
            apiMessage: 'Something went wrong while requesting inspections. Please try again.',
            isApiError: true,
          }),
        );
        setSubmitting(false);
        return;
      }

      // Merge the new status/date into both the cached permit list and the
      // currently-open permit so the details table reflects the change.
      const mergeInspection = (pi: any) => {
        if (updatedById[pi.id]) {
          return {
            ...pi,
            ...updatedById[pi.id],
            statusName: InspectionStatuses.READY_FOR_SCHEDULING,
            inspectionDate: formattedDate,
          };
        }
        return pi;
      };

      const updatedList = permitInspectionsList.map(p => {
        if (p.permitId === selectedPermit.permitId) {
          return { ...p, inspections: p.inspections.map(mergeInspection) };
        }
        return p;
      });
      dispatch(setPermitInspectionsList(updatedList));

      dispatch(
        setSelectedPermit({
          ...selectedPermit,
          inspections: (selectedPermit.inspections || []).map(mergeInspection),
        }),
      );

      const failedCount = results.length - successCount;
      dispatch(
        setInspectionApiResults({
          apiMessage:
            failedCount > 0
              ? `${successCount} of ${results.length} inspections requested. ${failedCount} could not be requested.`
              : `${successCount} ${successCount === 1 ? 'inspection' : 'inspections'} requested successfully.`,
          isApiError: false,
        }),
      );
      setDone(true);
      onSuccess();
    } catch (e) {
      dispatch(
        setInspectionApiResults({
          apiMessage: 'Something went wrong while requesting inspections. Please try again.',
          isApiError: true,
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const projectName = selectedPermit?.namePermitName || '—';
  const address = selectedPermit?.address || '—';
  const isContactsLoading = allContacts.length === 0;

  const submitDisabled =
    !done &&
    (submitting ||
      Object.keys(contact).length === 0 ||
      !inspectionDate ||
      disableFromHold);

  let submitLabel = `Request ${count} ${inspectionLabel}`;
  if (done) {
    submitLabel = 'Close';
  } else if (submitting) {
    submitLabel = 'Requesting…';
  }

  return (
    <ModalDefaultDraggable
      title="Request Inspections"
      onClose={onClose}
      wide
      addContactModal
    >
      <FormBody>
        <Root>
          <HeaderCard>
            <HeaderCol>
              <HeaderLabel>Project / Permit</HeaderLabel>
              <HeaderTitle title={projectName}>{projectName}</HeaderTitle>
              <HeaderAddress title={address}>{address}</HeaderAddress>
            </HeaderCol>
          </HeaderCard>

          <SectionEyebrow>
            Selected Inspections
            <CountPill>{count}</CountPill>
          </SectionEyebrow>

          <SelectedList>
            {selectedInspections.map(insp => {
              const { code, description } = splitTypeName(insp.activityTypeName);
              return (
                <SelectedRow key={insp.id}>
                  <Mono>{code || '—'}</Mono>
                  <SelectedDesc title={description}>
                    {description || insp.activityTypeName || '—'}
                  </SelectedDesc>
                  <SelectedStatus>{insp.statusName || '—'}</SelectedStatus>
                </SelectedRow>
              );
            })}
          </SelectedList>

          <SectionEyebrow>Request Details</SectionEyebrow>
          <TripleGrid>
            <Field>
              <FieldLabel error={!!errors.inspectionDate}>
                Inspection Date <Required>*</Required>
              </FieldLabel>
              <FieldShell error={!!errors.inspectionDate}>
                <DatePickerControl
                  value={inspectionDate ? new Date(inspectionDate) : null}
                  onChange={setDateValue}
                />
              </FieldShell>
              {errors.inspectionDate && (
                <ErrorText>{errors.inspectionDate}</ErrorText>
              )}
            </Field>

            <Field>
              <FieldLabel error={!!errors.contactPhone}>
                Phone Number <Required>*</Required>
              </FieldLabel>
              <FieldShell error={!!errors.contactPhone}>
                <ReactInputMask
                  mask="999-999-9999"
                  maskChar=" "
                  value={contactPhone}
                  onChange={onPhoneChange}
                >
                  {() => (
                    <PhoneInput
                      name="phone"
                      value={contactPhone}
                      onChange={onPhoneChange}
                      placeholder="###-###-####"
                    />
                  )}
                </ReactInputMask>
              </FieldShell>
              {errors.contactPhone && (
                <ErrorText>{errors.contactPhone}</ErrorText>
              )}
            </Field>

            <Field>
              <FieldLabel>
                Contact Name <Required>*</Required>
                {isContactsLoading && (
                  <InlineLoading>
                    <Loader size={12} thickness={5} />
                    <span>Loading contacts…</span>
                  </InlineLoading>
                )}
              </FieldLabel>
              <FieldShell hasNativeSelect>
                <ContactDropdown inspection={selectedInspections[0] || {}} />
              </FieldShell>
            </Field>
          </TripleGrid>

          <CriticalCallout role="alert">
            <CriticalIcon>
              <WarningIcon />
            </CriticalIcon>
            <CriticalText>
              <CriticalHeading>Time-frame requests will not be honored.</CriticalHeading>
              <CriticalSub>
                Inspectors are dispatched in route order — please do not request
                a specific time window in the notes.
              </CriticalSub>
            </CriticalText>
          </CriticalCallout>

          <NotesSection>
            <FieldLabel>Notes</FieldLabel>
            <InfoNote>
              <InfoOutlinedIcon style={{ fontSize: 16 }} />
              <span>
                These notes will be applied to every selected inspection.
              </span>
            </InfoNote>
            <NotesArea
              rows={5}
              value={notes || ''}
              onChange={e => dispatch(setNotes(e.target.value))}
              placeholder="Add any notes for the inspector…"
            />
          </NotesSection>

          {apiMessage && (
            <StatusBanner isApiError={isApiError}>{apiMessage}</StatusBanner>
          )}
        </Root>
      </FormBody>

      <StickyFooter>
        {disableFromHold && <HoldMessage>{HOLD_MESSAGE}</HoldMessage>}
        <FooterButtons>
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" disabled={submitDisabled} onClick={onSubmit}>
            {submitLabel}
          </PrimaryButton>
        </FooterButtons>
      </StickyFooter>
    </ModalDefaultDraggable>
  );
};

export default BulkRequestInspectionsPopup;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

const FormBody = styled.div`
  padding-bottom: 88px;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  box-sizing: border-box;
  font-family: ${FONT_STACK};
`;

const HeaderCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  font-size: 0.8125rem;
  color: var(--neutral-600);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SectionEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-top: 4px;
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const SelectedList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  max-height: 28vh;
  overflow-y: auto;
`;

const SelectedRow = styled.div`
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr) 140px;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: 0;
  }

  @media only screen and (max-width: 540px) {
    grid-template-columns: 64px minmax(0, 1fr);
    grid-row-gap: 4px;
  }
`;

const Mono = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  background: var(--neutral-100);
  border: 1px solid var(--border);
  padding: 2px 8px;
  border-radius: 6px;
  text-align: center;
`;

const SelectedDesc = styled.span`
  font-size: 0.875rem;
  color: var(--mainText);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedStatus = styled.span`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  text-align: right;

  @media only screen and (max-width: 540px) {
    display: none;
  }
`;

const TripleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 20px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const FieldLabel = styled.label<{ error?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ error }) => (error ? 'var(--danger-500)' : 'var(--mainText)')};
`;

const Required = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const FieldShell = styled.div<{ error?: boolean; hasNativeSelect?: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ error }) => (error ? 'var(--danger-500)' : 'var(--border)')};
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ error }) =>
      error ? 'var(--danger-500)' : 'var(--neutral-400)'};
  }

  &:focus-within {
    border-color: ${({ error }) =>
      error ? 'var(--danger-500)' : 'var(--primary-600)'};
    box-shadow: ${({ error }) => (error ? 'none' : 'var(--shadow-focus)')};
  }

  > div,
  > input,
  > select,
  .react-datepicker-wrapper,
  .react-datepicker__input-container,
  .react-datepicker__input-container input {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.875rem;
    color: var(--mainText);
    font-family: inherit;
  }

  select {
    cursor: pointer;
    appearance: ${({ hasNativeSelect }) => (hasNativeSelect ? 'auto' : 'none')};
  }

  input::placeholder {
    color: var(--neutral-400);
  }
`;

const PhoneInput = styled.input`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--mainText);
  font-family: inherit;
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: var(--danger-500);
  margin-top: 2px;
`;

const InlineLoading = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--neutral-500);
  vertical-align: middle;
`;

const CriticalCallout = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-left: 4px solid #dc2626;
  box-shadow: 0 1px 2px rgba(220, 38, 38, 0.08);
`;

const CriticalIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fee2e2;
  color: #dc2626;
  flex-shrink: 0;

  svg {
    font-size: 22px;
  }
`;

const CriticalText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CriticalHeading = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: #991b1b;
  letter-spacing: 0.01em;
`;

const CriticalSub = styled.div`
  font-size: 0.8125rem;
  color: #7f1d1d;
  line-height: 1.45;
`;

const NotesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
`;

const InfoNote = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--neutral-600);
  line-height: 1.45;

  svg {
    color: var(--neutral-500);
    margin-top: 1px;
    flex-shrink: 0;
  }
`;

const NotesArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const StatusBanner = styled.div<{ isApiError: boolean }>`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
  background: ${({ isApiError }) => (isApiError ? 'var(--danger-50)' : '#dcfce7')};
  color: ${({ isApiError }) => (isApiError ? 'var(--danger-700)' : '#166534')};
  border: 1px solid
    ${({ isApiError }) => (isApiError ? 'var(--danger-200, #fecaca)' : '#bbf7d0')};
`;

const StickyFooter = styled.div`
  position: sticky;
  bottom: -35px;
  margin: 0 -35px -35px;
  padding: 14px 28px;
  background: var(--neutral-0);
  border-top: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2;
`;

const FooterButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const HoldMessage = styled.div`
  font-size: 0.8125rem;
  color: var(--danger-700, #991b1b);
  background: var(--danger-50, #fee2e2);
  border: 1px solid var(--danger-200, #fecaca);
  border-radius: 6px;
  padding: 8px 12px;
  text-align: center;
`;

const PrimaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    background: var(--neutral-300);
    color: var(--neutral-0);
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  min-width: 100px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
